import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Navbar, Sidebar } from "@/components/layout";
import { ErrorBoundary, RouteError } from "@/components/error";
import { useAuthStore } from "@/features/auth";
import { PATHS } from "@/lib/router";
import { ROLES } from "@/lib/constants/options";

const AppLayout = observer(() => {
  const location = useLocation();
  const { user } = useAuthStore();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const isCompanySetupPath = location.pathname === PATHS.COMPANY_SETUP;
  const isESGReportPath = location.pathname.startsWith("/esg/report/");
  const isAdminRole = [ROLES.SYSTEM_ADMIN, ROLES.BUSINESS_UNIT].includes(
    user?.role,
  );
  const needsCompanySetup = Boolean(user && !isAdminRole && !user?.companyId);
  const hideChrome = isCompanySetupPath && needsCompanySetup;

  if (hideChrome) {
    return (
      <div className="min-h-screen bg-background">
        <main className="min-h-screen bg-background">
          <div className="container mx-auto p-4">
            <ErrorBoundary fallback={RouteError}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    );
  }

  const mainOverflowClass = isESGReportPath
    ? "overflow-hidden"
    : "overflow-y-auto";
  const containerOverflowClass = isESGReportPath
    ? "overflow-hidden"
    : "overflow-y-auto";
  const containerHeightClass = isESGReportPath
    ? "min-h-0 h-full"
    : "min-h-full";

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop Sidebar */}
      {!isESGReportPath && (
        <div className="hidden lg:block">
          <Sidebar
            collapsed={sidebarCollapsed}
            onCollapseChange={setSidebarCollapsed}
          />
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="relative h-full w-64 shadow-lg">
            <Sidebar
              collapsed={false}
              onCollapseChange={() => setMobileSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Navbar */}
        <Navbar
          sidebarCollapsed={sidebarCollapsed}
          onMobileMenuToggle={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        />

        {/* Page Content */}
        <main
          className={`flex-1 bg-background p-4 h-full ${mainOverflowClass}`}
        >
          <div
            className={`2xl:container mx-auto p-4 bg-card/70 rounded-radius flex flex-col ${containerHeightClass} ${containerOverflowClass}`}
          >
            <ErrorBoundary fallback={RouteError}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
});

export default AppLayout;
